import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-400 ease-smooth focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-body tracking-wider",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-border bg-transparent text-foreground shadow-sm hover:bg-muted hover:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: 
          "text-foreground hover:bg-muted hover:text-foreground",
        link: 
          "text-crimson underline-offset-4 hover:underline",
        // Luxury Neon variants
        neon:
          "bg-crimson text-primary-foreground font-semibold uppercase tracking-[0.2em] shadow-neon hover:shadow-crimson",
        neonOutline:
          "border border-crimson/50 bg-transparent text-crimson uppercase tracking-[0.2em] hover:bg-crimson/10 hover:border-crimson hover:shadow-neon",
        neonGhost:
          "bg-transparent text-ivory uppercase tracking-[0.2em] hover:text-crimson",
        gold:
          "bg-gold text-background font-semibold uppercase tracking-[0.2em] hover:shadow-gold",
        goldOutline:
          "border border-gold/50 bg-transparent text-gold uppercase tracking-[0.2em] hover:bg-gold/10 hover:border-gold",
        play:
          "bg-crimson text-primary-foreground rounded-full hover:scale-105 shadow-neon animate-glow-pulse",
        minimal:
          "bg-muted/30 text-foreground border border-border/50 hover:bg-muted/50 hover:border-border",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-8 px-4 text-xs",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg",
        icon: "h-10 w-10",
        iconSm: "h-8 w-8",
        iconLg: "h-14 w-14",
        iconXl: "h-16 w-16",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
